module Main exposing (CommonData, Config, Model, main)

import Api
import Api.Agreements as Agreements
import Api.Locale as I18N
import Api.TsBackend as TsBackend
import Api.User.Extra as User
import Browser
import Browser.Dom as Dom
import Browser.Events as Browser
import Cmd.Extra as Cmd
import Date exposing (Date)
import Element exposing (..)
import Element.Background as Background
import Element.Events as Events
import Element.Font as Font
import Element.Region as Region
import Ext.Browser
import Ext.Browser.Navigation as Nav
import Extra exposing (..)
import Focus
import Html
import Html.Attributes
import I18N
import I18N.Main as I18N
import Json.Decode as Decode
import Maybe.Extra as Maybe
import Menu exposing (LeftSidebarMenuState, SideMenuState, TopMenuState)
import Menu.LeftSidebarMenu as LeftSidebarMenu
import Menu.ModalMenu as ModalMenu
import Menu.SideMenu as SideMenu
import Menu.TopMenu as TopMenu
import MetaTags
import Modal
import Page exposing (Msg(..), Page)
import Page.Administrators
import Page.AssignmentOverview
import Page.ConsultantCompanies
import Page.Consultants
import Page.EditAccount as EditAccount
import Page.Footer as Footer
import Page.Home as Home
import Page.Home.View as Home
import Page.Invitation
import Page.ProfileComparison as ProfileComparison
import Page.UIExplorer
import Page.Verified as Verified
import Ports.HtmlLang
import Ports.LocalStorage
import ProfileRoute
import RemoteData
import Route
import Scroll
import ScrollPort
import Session
import Task
import Time exposing (Posix)
import Type.Component.Msg as Component
import Type.Document as Document
import Type.Flags as Flags
import Type.Screen as Screen exposing (Screen)
import Type.Session as Session exposing (Session)
import Type.Window exposing (Window)
import UI
import UI.Color
import UI.Font
import UI.Link as Link
import UI.OnPress exposing (OnPressTarget(..))
import UI.Transitions
import Url exposing (Url)
import Url.Parts
import WebData exposing (WebData)


main : Program Decode.Value Model Msg
main =
    Browser.element
        { init = init
        , view = view
        , update = update
        , subscriptions = subscriptions
        }


init : Decode.Value -> ( Model, Cmd Msg )
init json =
    let
        { window, time, zone, spaFlags, browserLocales, folqAuthCookieExists, leftSidebarMenuIsCollapsed } =
            Flags.decode json

        { url, navKey } =
            Nav.spaFlags { spaFlags = spaFlags }

        locale =
            Session.getUserLocaleWithDefault browserLocales url Session.None

        config =
            { key = navKey
            , landingUrl = url
            , baseUrl = Url.Parts.toBase url
            , zone = zone
            , initTime = time
            , today = Date.fromPosix zone time
            , window = window
            , screen = Screen.fromWindow window
            , urlChangeTriggeredFromLinkClick = False
            , locale = locale
            , tsBackendAccountAndAccesses = RemoteData.NotAsked
            , tsBackendAllOrganizationFeatureAccesses = RemoteData.NotAsked
            , hasSignedCooperationContract = RemoteData.NotAsked
            , folqAuthCookieExists = folqAuthCookieExists
            , leftSidebarMenuState =
                if leftSidebarMenuIsCollapsed then
                    Menu.Collapsed

                else
                    Menu.Regular
            }

        ( page, pageCmd ) =
            Page.initFrom config
                { oldPage = Nothing
                , session = Session.None
                , url = url
                , wasLoggedOut = False
                }

        ( menu, menuCmd ) =
            let
                shouldOpenInviteAColleagueModal =
                    Maybe.unwrap False (String.contains Route.openInviteAColleagueQueryString) url.query
            in
            Menu.init Session.None { shouldOpenInviteAColleagueModal = shouldOpenInviteAColleagueModal }

        ( sessionModel, sessionModelCmd ) =
            Session.init config Session.None

        model =
            { config = config
            , url = url
            , reloadEntireAppOnNextOccasion = False
            , scroll = { y = 0 }
            , page = page
            , menu = menu
            , commonData = { unreadMessageCount = RemoteData.NotAsked, hasMessageThreads = RemoteData.NotAsked, unreadNotificationCount = RemoteData.NotAsked }
            , sessionModel = sessionModel
            , showSkipLink = False
            , visibility = Browser.Visible
            , sideMenuState = Menu.Closed
            , topMenuState = Menu.Visible
            }
    in
    ( model
    , Cmd.batch
        [ pageCmd
        , Cmd.map GotMenuMsg menuCmd
        , getUnreadMessageCount <| Page.toSession page
        , getHasMessageThreads <| Page.toSession page
        , Focus.onLoad
        , MetaTags.update <| Page.toTags config url
        , sessionModelCmd
        , Ports.HtmlLang.setHtmlLang <| I18N.localeToString locale
        ]
    )


{-| Pretty much static data, so safe to use as an argument to lazy elements
-}
type alias Config =
    { key : Nav.Key
    , landingUrl : Url.Url
    , baseUrl : Url.Parts.Base
    , zone : Time.Zone
    , initTime : Posix
    , today : Date
    , window : Window
    , screen : Screen
    , urlChangeTriggeredFromLinkClick : Bool
    , locale : I18N.Locale
    , tsBackendAccountAndAccesses : WebData TsBackend.Account
    , tsBackendAllOrganizationFeatureAccesses : WebData (List TsBackend.OrganizationFeatureAccess)
    , hasSignedCooperationContract : WebData Agreements.HasSignedCooperationContractResponse
    , folqAuthCookieExists : Bool
    , leftSidebarMenuState : LeftSidebarMenuState
    }


type alias Model =
    { config : Config
    , url : Url
    , reloadEntireAppOnNextOccasion : Bool
    , scroll : { y : Int }
    , page : Page
    , menu : Menu.Model
    , commonData : CommonData
    , sessionModel : Session.Model
    , showSkipLink : Bool
    , visibility : Browser.Visibility
    , sideMenuState : SideMenuState
    , topMenuState : TopMenuState
    }


type alias CommonData =
    { unreadMessageCount : WebData Int
    , hasMessageThreads : WebData Bool
    , unreadNotificationCount : WebData Int
    }


subscriptions : Model -> Sub Msg
subscriptions model =
    Sub.batch
        [ Browser.onResize Resized
        , Nav.onUrlRequest LinkClicked
        , Nav.onUrlChange UrlChanged
        , Browser.onVisibilityChange VisibilityChanged
        , ScrollPort.scrollY <| ScrolledY << round
        , Sub.map GotSessionMsg <| Session.subscriptions model.visibility
        , Sub.map GotMenuMsg <| Menu.subscriptions model.visibility (Page.toSession model.page) model.menu model.sideMenuState
        , case model.visibility of
            Browser.Hidden ->
                Sub.none

            Browser.Visible ->
                Time.every (10 * 1000) <| always UpdateUnreadMessageCount
        , case model.page of
            Page.AssignmentOverview assignmentOverview ->
                Sub.map GotAssignmentOverviewMsg <| Page.AssignmentOverview.subscriptions assignmentOverview

            Page.Home _ ->
                Sub.none

            Page.Verified _ ->
                Sub.none

            Page.Invitation _ ->
                Sub.none

            Page.ProfileComparison _ ->
                Sub.none

            Page.Administrators administrators ->
                Sub.map GotAdministratorsMsg <| Page.Administrators.subscriptions administrators

            Page.Consultants consultants ->
                Sub.map GotConsultantsMsg <| Page.Consultants.subscriptions consultants

            Page.ConsultantCompanies consultantCompanies ->
                Sub.map GotConsultantCompaniesMsg <| Page.ConsultantCompanies.subscriptions consultantCompanies

            Page.EditAccount editAccount ->
                Sub.map GotEditAccountMsg <| EditAccount.subscriptions editAccount

            Page.UIExplorer _ ->
                Sub.none
        ]


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    let
        config =
            model.config

        ignore =
            ( model, Cmd.none )

        updateTopMenuState y model_ =
            let
                scrolledUp =
                    y < model.scroll.y

                scrolledDown =
                    y > model.scroll.y
            in
            { model_
                | topMenuState =
                    if scrolledUp then
                        Menu.Visible

                    else if scrolledDown then
                        Menu.Hidden

                    else
                        model.topMenuState
            }
    in
    case ( msg, model.page ) of
        ( DomTasks _, _ ) ->
            ignore

        ( LinkClicked urlRequest, _ ) ->
            let
                modelWithoutReloadEntireAppOnNextOccasion =
                    { model | reloadEntireAppOnNextOccasion = False }
            in
            case urlRequest of
                Ext.Browser.Internal url ->
                    ( { modelWithoutReloadEntireAppOnNextOccasion | config = { config | urlChangeTriggeredFromLinkClick = True } }
                    , Cmd.batch
                        [ if model.reloadEntireAppOnNextOccasion then
                            Nav.load <| Url.toString url

                          else
                            Nav.pushUrl config.key <| Url.toString url
                        , scrollToTopOrFragmentElement url.fragment
                        , Focus.onLoad
                        ]
                    )

                Ext.Browser.External href ->
                    ( modelWithoutReloadEntireAppOnNextOccasion
                    , Nav.load href
                    )

        ( UrlChanged ( shouldRunInit, url ), page ) ->
            let
                newSession =
                    Page.toSession model.page

                ( newPage, cmds ) =
                    if shouldRunInit then
                        Page.initFrom
                            config
                            { oldPage = Just model.page
                            , session = newSession
                            , url = url
                            , wasLoggedOut = False
                            }

                    else
                        ( page, Cmd.none )

                document =
                    getDocument config model newPage

                title =
                    case model.commonData.unreadMessageCount of
                        RemoteData.Success 0 ->
                            document.title

                        RemoteData.Success unreadMessages ->
                            "(" ++ String.fromInt unreadMessages ++ ") " ++ document.title

                        _ ->
                            document.title

                ( menu, menuCmd ) =
                    if shouldRunInit then
                        -- Initialize the menu with possible open modal state if the user was just logged in (i.e. came from Home.Callback)
                        case page of
                            Page.Home { subPage } ->
                                case subPage of
                                    Home.Callback _ ->
                                        Menu.init newSession { shouldOpenInviteAColleagueModal = Maybe.unwrap False (String.contains Route.openInviteAColleagueQueryString) url.query }

                                    _ ->
                                        ( Menu.closeModalMenu model.menu, Cmd.none )

                            _ ->
                                ( Menu.closeModalMenu model.menu, Cmd.none )

                    else
                        ( model.menu, Cmd.none )
            in
            ( { model
                | page = newPage
                , menu = menu
                , url = url
                , sideMenuState = Menu.Closed
                , config = { config | urlChangeTriggeredFromLinkClick = False }
              }
            , Cmd.batch
                [ cmds
                , MetaTags.update <| Page.toTags config url
                , Ext.Browser.setPageTitle title
                ]
            )
                |> Cmd.addCmd (Cmd.map GotMenuMsg menuCmd)

        ( GotSessionMsg (Component.Internal internalMsg), _ ) ->
            Session.update
                config
                (Page.toSession model.page)
                internalMsg
                model.sessionModel
                |> Tuple.mapBoth
                    (\sessionModel -> { model | sessionModel = sessionModel })
                    (Cmd.map GotSessionMsg)

        ( GotSessionMsg (Component.External (Session.GotFolqAuthCookieExistsStatus folqAuthCookieExists)), _ ) ->
            ( { model | config = { config | folqAuthCookieExists = folqAuthCookieExists } }, Cmd.none )

        ( GotSessionMsg (Component.External (Session.GotNewSession newSession)), _ ) ->
            let
                oldSession =
                    Page.toSession model.page

                wasLoggedOut =
                    case ( oldSession, newSession ) of
                        ( Session.LoggedIn _, Session.None ) ->
                            True

                        _ ->
                            False

                loginState =
                    case ( oldSession, newSession ) of
                        ( Session.None, Session.LoggedIn login ) ->
                            let
                                organizationFeatureAccessCmd =
                                    if User.isAdmin login.user then
                                        getAllOrganizationFeatureAccess

                                    else
                                        Cmd.none
                            in
                            { locale = login.user.locale
                            , tsBackendAccountAndAccesses = RemoteData.Loading
                            , tsBackendAllOrganizationFeatureAccesses =
                                if User.isAdmin login.user then
                                    RemoteData.Loading

                                else
                                    RemoteData.NotAsked
                            , tsBackendDataCmd =
                                Cmd.batch [ getAccountAndAccesses, organizationFeatureAccessCmd ]
                            , hasSignedCooperationContract = RemoteData.Loading
                            , hasSignedCooperationContractCmd = getHasSignedCooperationContract
                            }

                        _ ->
                            { locale = config.locale
                            , tsBackendAccountAndAccesses = config.tsBackendAccountAndAccesses
                            , tsBackendAllOrganizationFeatureAccesses = config.tsBackendAllOrganizationFeatureAccesses
                            , tsBackendDataCmd = Cmd.none
                            , hasSignedCooperationContract = config.hasSignedCooperationContract
                            , hasSignedCooperationContractCmd = Cmd.none
                            }

                ( menu, menuCmd ) =
                    Menu.init newSession { shouldOpenInviteAColleagueModal = Maybe.unwrap False (String.contains Route.openInviteAColleagueQueryString) config.landingUrl.query }
            in
            Page.initFrom
                config
                { oldPage = Nothing
                , session = newSession
                , url = model.url
                , wasLoggedOut = wasLoggedOut
                }
                |> Tuple.mapFirst
                    (\page ->
                        { model
                            | config =
                                { config
                                    | locale = loginState.locale
                                    , tsBackendAccountAndAccesses = loginState.tsBackendAccountAndAccesses
                                    , tsBackendAllOrganizationFeatureAccesses = loginState.tsBackendAllOrganizationFeatureAccesses
                                    , hasSignedCooperationContract = loginState.hasSignedCooperationContract
                                }
                            , menu = menu
                            , page = page
                        }
                    )
                |> Cmd.addCmd (Cmd.map GotMenuMsg menuCmd)
                |> Cmd.addCmd loginState.tsBackendDataCmd
                |> Cmd.addCmd loginState.hasSignedCooperationContractCmd
                |> Cmd.addCmd (getHasMessageThreads newSession)

        ( GotSessionMsg (Component.External Session.NewApiVersionDetected), _ ) ->
            ( { model | reloadEntireAppOnNextOccasion = True }, Cmd.none )

        ( Resized x y, _ ) ->
            let
                window =
                    { width = x, height = y }
            in
            ( { model | config = { config | window = window, screen = Screen.fromWindow window } }, Cmd.none )

        ( VisibilityChanged visibility, _ ) ->
            ( { model | visibility = visibility }, Cmd.map GotSessionMsg <| Session.onVisibilityChanged visibility )

        ( ScrolledY y, _ ) ->
            ( { model | scroll = { y = y } }, Cmd.none )
                |> Tuple.mapFirst (updateTopMenuState y)

        ( HasMessageThreadsResponse response, _ ) ->
            let
                commonData_ =
                    model.commonData
            in
            ( { model | commonData = { commonData_ | hasMessageThreads = response } }, Cmd.none )

        ( UnreadMessageCountResponse response, _ ) ->
            let
                commonData_ =
                    model.commonData
            in
            ( { model | commonData = { commonData_ | unreadMessageCount = response } }, Cmd.none )

        ( UpdateUnreadMessageCount, page ) ->
            let
                hasMessageThreadsCmd =
                    case model.commonData.hasMessageThreads of
                        RemoteData.Success True ->
                            Cmd.none

                        _ ->
                            getHasMessageThreads <| Page.toSession page
            in
            ( model
            , Cmd.batch
                [ getUnreadMessageCount <| Page.toSession page
                , hasMessageThreadsCmd
                , getUnreadNotificationCountCmd (Page.toSession page)
                ]
            )

        ( ShowSkipLink s, _ ) ->
            ( { model | showSkipLink = s }, Cmd.none )

        ( GotMenuMsg (Component.External externalMsg), _ ) ->
            case externalMsg of
                Menu.OpenSideMenu ->
                    ( { model | sideMenuState = Menu.Open }
                    , Cmd.none
                    )

                Menu.CloseSideMenu ->
                    ( { model | sideMenuState = Menu.Closed }
                    , Cmd.none
                    )

                Menu.ToggleLeftSidebarMenu ->
                    let
                        newState =
                            if config.leftSidebarMenuState == Menu.Regular then
                                Menu.Collapsed

                            else
                                Menu.Regular
                    in
                    ( { model
                        | config = { config | leftSidebarMenuState = newState }
                      }
                    , Ports.LocalStorage.setItem
                        ( Ports.LocalStorage.localStorageTypeToString Ports.LocalStorage.LeftSidebarMenuStore
                        , case newState of
                            Menu.Regular ->
                                "regular"

                            Menu.Collapsed ->
                                "collapsed"
                        )
                    )

                Menu.SetLocale locale ->
                    let
                        config_ =
                            model.config
                    in
                    ( { model | config = { config_ | locale = locale } }
                    , Ports.HtmlLang.setHtmlLang <| I18N.localeToString locale
                    )

        ( GotMenuMsg (Component.Internal internalMsg), _ ) ->
            Menu.update config { session = Page.toSession model.page } internalMsg model.menu
                |> updateWith (\menu -> { model | menu = menu }) GotMenuMsg

        ( GotHomeMsg subMsg, Page.Home home ) ->
            Home.update config subMsg home
                |> updateWith Page.Home GotHomeMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotHomeMsg _, _ ) ->
            ignore

        ( GotInvitationMsg subMsg, Page.Invitation invitation ) ->
            Page.Invitation.update config subMsg invitation
                |> updateWith Page.Invitation GotInvitationMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotInvitationMsg _, _ ) ->
            ignore

        ( GotUIExplorerMsg _, _ ) ->
            ignore

        ( GotProfileComparisonMsg subMsg, Page.ProfileComparison profileComparison ) ->
            ProfileComparison.update subMsg profileComparison
                |> updateWith Page.ProfileComparison GotProfileComparisonMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotProfileComparisonMsg _, _ ) ->
            ignore

        ( GotAdministratorsMsg subMsg, Page.Administrators administrators ) ->
            Page.Administrators.update model subMsg administrators
                |> updateWith Page.Administrators GotAdministratorsMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotAdministratorsMsg _, _ ) ->
            ignore

        ( GotConsultantsMsg subMsg, Page.Consultants consultants ) ->
            Page.Consultants.update model subMsg consultants
                |> updateWith Page.Consultants GotConsultantsMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotConsultantsMsg _, _ ) ->
            ignore

        ( GotConsultantCompaniesMsg subMsg, Page.ConsultantCompanies companies ) ->
            Page.ConsultantCompanies.update model subMsg companies
                |> updateWith Page.ConsultantCompanies GotConsultantCompaniesMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotConsultantCompaniesMsg _, _ ) ->
            ignore

        ( GotEditAccountMsg subMsg, Page.EditAccount editAccount ) ->
            EditAccount.update config subMsg editAccount
                |> updateWith Page.EditAccount GotEditAccountMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotEditAccountMsg _, _ ) ->
            ignore

        ( GotAssignmentOverviewMsg subMsg, Page.AssignmentOverview assignment ) ->
            Page.AssignmentOverview.update config subMsg assignment
                |> updateWith Page.AssignmentOverview GotAssignmentOverviewMsg
                |> Tuple.mapFirst (asPageIn model)

        ( GotAssignmentOverviewMsg _, _ ) ->
            ignore

        ( GotAccountAndAccesses response, _ ) ->
            let
                commonData_ =
                    model.commonData

                unreadNotificationCount_ =
                    case response of
                        RemoteData.Success _ ->
                            RemoteData.Loading

                        _ ->
                            RemoteData.NotAsked
            in
            ( { model
                | config = { config | tsBackendAccountAndAccesses = response }
                , commonData = { commonData_ | unreadNotificationCount = unreadNotificationCount_ }
              }
            , if TsBackend.isAccessRevoked response then
                Nav.load <| ProfileRoute.toStringUrl <| ProfileRoute.AccessRevoked config.locale

              else
                getUnreadNotificationCountCmd (Page.toSession model.page)
            )

        ( GotAllOrganizationFeatureAccesses response, _ ) ->
            ( { model | config = { config | tsBackendAllOrganizationFeatureAccesses = response } }, Cmd.none )

        ( UnreadNotificationCountResponse response, _ ) ->
            let
                commonData_ =
                    model.commonData
            in
            ( { model | commonData = { commonData_ | unreadNotificationCount = response } }, Cmd.none )

        ( GotHasSignedCooperationContract response, _ ) ->
            ( { model | config = { config | hasSignedCooperationContract = response } }, Cmd.none )

        ( NoOp, _ ) ->
            ignore


getUnreadMessageCount : Session -> Cmd Msg
getUnreadMessageCount session =
    case session of
        Session.None ->
            Cmd.none

        Session.LoggedIn _ ->
            Cmd.map (RemoteData.fromResult >> UnreadMessageCountResponse) Api.getThreadsUnread


getHasMessageThreads : Session -> Cmd Msg
getHasMessageThreads session =
    case session of
        Session.None ->
            Cmd.none

        Session.LoggedIn login ->
            if User.isConsultantBuyer login.user then
                Cmd.map
                    (RemoteData.fromResult
                        >> RemoteData.map (not << List.isEmpty)
                        >> HasMessageThreadsResponse
                    )
                    Api.getThreadsSimple

            else
                Cmd.none


getUnreadNotificationCountCmd : Session -> Cmd Msg
getUnreadNotificationCountCmd session =
    case session of
        Session.None ->
            Cmd.none

        Session.LoggedIn _ ->
            Cmd.map (RemoteData.fromResult >> RemoteData.map (\r -> r.count) >> UnreadNotificationCountResponse) TsBackend.getUnreadNotificationCount


getAccountAndAccesses : Cmd Msg
getAccountAndAccesses =
    Cmd.map (RemoteData.fromResult >> GotAccountAndAccesses) TsBackend.getAccountAndAccesses


getAllOrganizationFeatureAccess : Cmd Msg
getAllOrganizationFeatureAccess =
    Cmd.map (RemoteData.fromResult >> GotAllOrganizationFeatureAccesses) TsBackend.getAllOrganizationFeatureAccess


getHasSignedCooperationContract : Cmd Msg
getHasSignedCooperationContract =
    Cmd.map (RemoteData.fromResult >> GotHasSignedCooperationContract) Agreements.getHasSignedCooperationContract


asPageIn : Model -> Page -> Model
asPageIn model page =
    { model | page = page }


scrollToTopOrFragmentElement : Maybe String -> Cmd Msg
scrollToTopOrFragmentElement maybeFragment =
    case maybeFragment of
        Nothing ->
            Scroll.toTop |> Task.attempt DomTasks

        Just fragment ->
            Cmd.batch
                [ Dom.getElement fragment
                    |> Task.andThen
                        (\{ element } ->
                            Scroll.to (element.y - 130)
                        )
                    |> Task.attempt DomTasks
                , Focus.focusTemporarily fragment
                ]


view : Model -> Html.Html Msg
view ({ config, page, scroll, menu, showSkipLink } as model) =
    let
        { screen } =
            config

        menuModalView =
            Modal.map GotMenuMsg <| ModalMenu.view config page menu model.commonData

        t =
            I18N.usingLocale config.locale

        document =
            getDocument config model page

        isOpenPage =
            case page of
                Page.Home _ ->
                    True

                _ ->
                    False

        menuElement =
            Maybe.unwrap none (Element.map GotMenuMsg << TopMenu.view config screen scroll page model.topMenuState model.commonData) document.menu

        session =
            Page.toSession model.page

        footer =
            if document.footer then
                Footer.view config session { isOpenPage = isOpenPage }

            else
                none

        mainContentId =
            "main-content"

        skipLink =
            el
                (if showSkipLink then
                    [ padding 15, Background.color UI.Color.white ]

                 else
                    [ width <| px 0, height <| px 0, clip ]
                )
            <|
                Link.text
                    [ Font.size 18
                    , Events.onFocus <| ShowSkipLink True
                    , Events.onLoseFocus <| ShowSkipLink False
                    ]
                    { onPress = UI.OnPress.Open <| UsingStringUrl <| "#" ++ mainContentId
                    , label = t.tilHovedinnhold
                    }

        skipLinkElement =
            if Screen.isSmallScreen screen then
                skipLink

            else
                -- Must be the same as or higher than the side menu, which has zIndex 21 to be in front of inFront/onRight/onLeft elements, which have zIndex 20
                Element.el [ Element.inFront skipLink, UI.zIndex 21 ] Element.none

        addBackdropIf condition =
            inFront <|
                el
                    ([ height fill
                     , width fill
                     , UI.Transitions.fastTransition [ "background-color", "visibility" ]
                     , UI.zIndex 21
                     ]
                        ++ (if condition then
                                [ Background.color <| UI.Color.withAlpha 0.25 UI.Color.black
                                , Element.mapAttribute GotMenuMsg <| Events.onClick <| Component.External Menu.CloseSideMenu
                                ]

                            else
                                UI.hidden
                           )
                    )
                    none

        -- If we always use row, some pages look ugly when window width is less than 1450px.
        -- See https://github.com/folq/web/issues/3455 and https://github.com/folq/web/issues/7543
        ( ( leftSidebarMenu, sideMenu ), ( rowOrColumn, mainContentAttributes ) ) =
            let
                desktopAndLoggedIn =
                    ( ( Element.map GotMenuMsg <| LeftSidebarMenu.view config model.commonData page
                      , Element.map GotMenuMsg <|
                            SideMenu.view config { hasMessageThreads = model.commonData.hasMessageThreads } page model.sideMenuState menu
                      )
                    , ( row
                      , [ Element.htmlAttribute <|
                            Html.Attributes.style "max-width"
                                ("calc(100% - "
                                    ++ String.fromInt
                                        (case config.leftSidebarMenuState of
                                            Menu.Regular ->
                                                Menu.leftSidebarMenuRegularWidth

                                            Menu.Collapsed ->
                                                Menu.leftSidebarMenuCollapsedWidth
                                        )
                                    ++ "px)"
                                )
                        ]
                      )
                    )

                mobileOrNotLoggedIn =
                    ( ( none, none ), ( column, [] ) )
            in
            if Screen.isSmallScreen screen || Maybe.isNothing document.menu then
                mobileOrNotLoggedIn

            else
                case page of
                    Page.Home _ ->
                        mobileOrNotLoggedIn

                    _ ->
                        case Page.toSession page of
                            Session.LoggedIn _ ->
                                desktopAndLoggedIn

                            Session.None ->
                                mobileOrNotLoggedIn

        preventScrollWhenSideMenuIsOpenOnDesktopAttributes =
            case ( model.sideMenuState, Screen.isLargeScreen config.screen ) of
                ( Menu.Open, True ) ->
                    [ Html.Attributes.style "overflow-y" "hidden" ]

                _ ->
                    []
    in
    Html.div preventScrollWhenSideMenuIsOpenOnDesktopAttributes
        [ layout UI.Font.bodyM <|
            Modal.with menuModalView <|
                Modal.with document.modal <|
                    rowOrColumn [ width fill, height fill ]
                        [ leftSidebarMenu
                        , column
                            ([ width fill
                             , height fill
                             , addBackdropIf (model.sideMenuState == Menu.Open)
                             , UI.style "scrollbar-x" "hidden"
                             ]
                                ++ mainContentAttributes
                            )
                            [ skipLinkElement
                            , menuElement
                            , el [ Region.mainContent, UI.id mainContentId, width fill, height fill ] document.body
                            , footer
                            ]
                        , sideMenu
                        ]
        ]


getDocument : Config -> Model -> Page -> Document.Document Msg
getDocument config model page =
    case page of
        Page.Home home ->
            Document.map GotHomeMsg <| Home.view config model.url home

        Page.Verified verified ->
            Verified.view config.locale verified

        Page.Invitation invitation ->
            Document.map GotInvitationMsg <| Page.Invitation.view config invitation

        Page.UIExplorer _ ->
            Document.map GotUIExplorerMsg <| Page.UIExplorer.view config

        Page.Administrators administrators ->
            Document.map GotAdministratorsMsg <| Page.Administrators.view config administrators

        Page.Consultants consultants ->
            Document.map GotConsultantsMsg <| Page.Consultants.view config consultants

        Page.ConsultantCompanies companies ->
            Document.map GotConsultantCompaniesMsg <| Page.ConsultantCompanies.view config companies

        Page.EditAccount editAccount ->
            Document.map GotEditAccountMsg <| EditAccount.view config editAccount

        Page.ProfileComparison profileComparison ->
            Document.map GotProfileComparisonMsg <| ProfileComparison.view config profileComparison

        Page.AssignmentOverview assignment ->
            Document.map GotAssignmentOverviewMsg <| Page.AssignmentOverview.view config assignment
